import { useEffect, useRef, useState, type ComponentProps, type Key } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import type { ManagerRole } from '../api/managerApi';
import DataTable, { type DataTableColumn } from '../components/common/DataTable';
import { LoadingState } from '../components/common';
import {
  RichTextContent,
  RichTextEditor,
  sanitizeRichTextHtml,
} from '../components/common/RichTextEditor';
import { loadMyManagerProfile } from '../services/managerService';
import {
  announcementService,
  type Announcement,
  type AnnouncementPayload,
} from '../services/announcementService';

type NoticeFormState = AnnouncementPayload;
type NoticeCategoryFilter = 'ALL' | 'NOTICE' | 'FAQ';
type NoticeCategory = Exclude<NoticeCategoryFilter, 'ALL'>;

const INITIAL_VISIBLE_COUNT = 20;
const VISIBLE_COUNT_STEP = 20;

const emptyForm: NoticeFormState = {
  title: '',
  content: '',
  category: 'NOTICE',
};

const NOTICE_CATEGORY_OPTIONS: { label: string; value: NoticeCategory }[] = [
  { label: '공지사항', value: 'NOTICE' },
  { label: 'FAQ', value: 'FAQ' },
];

const NOTICE_CATEGORY_FILTERS: { label: string; value: NoticeCategoryFilter }[] = [
  { label: '전체', value: 'ALL' },
  ...NOTICE_CATEGORY_OPTIONS,
];

function getCategoryLabel(category: string) {
  return NOTICE_CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? category;
}

function EditIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="16"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="16"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function formatDate(value?: string) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('ko-KR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function getAuthor(announcement: Announcement) {
  return (
    announcement.writer ??
    announcement.author ??
    announcement.managerName ??
    `관리자 ${announcement.managerId}`
  );
}

function getCreatedAt(announcement: Announcement) {
  return announcement.createdAt ?? announcement.createdDate ?? announcement.createdDateTime;
}

function toPlainText(html: string) {
  const parser = new DOMParser();
  const document = parser.parseFromString(sanitizeRichTextHtml(html), 'text/html');
  return document.body.textContent?.replace(/\s+/g, ' ').trim() ?? '';
}

function isRichTextEmpty(html: string) {
  return toPlainText(html).length === 0;
}

function isEditMode(mode: string | null): mode is 'edit' {
  return mode === 'edit';
}

function isDetailMode(mode: string | null): mode is 'detail' {
  return mode === 'detail';
}

function getAnnouncementListState(
  announcements: Announcement[],
  categoryFilter: NoticeCategoryFilter,
  visibleCount: number
) {
  const filteredAnnouncements =
    categoryFilter === 'ALL'
      ? announcements
      : announcements.filter((announcement) => announcement.category === categoryFilter);

  return {
    filteredAnnouncements,
    hasMoreAnnouncements: visibleCount < filteredAnnouncements.length,
    visibleAnnouncements: filteredAnnouncements.slice(0, visibleCount),
  };
}

type NoticeDetailContentProps = Readonly<{
  announcement: Announcement | null;
  loading: boolean;
}>;

function NoticeDetailContent({ announcement, loading }: NoticeDetailContentProps) {
  if (loading) {
    return <LoadingState message="공지사항을 불러오는 중입니다." />;
  }

  if (!announcement) {
    return (
      <Box sx={{ color: '#64748b', py: 6 }}>
        공지사항 상세 정보를 찾을 수 없습니다.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderLeft: '4px solid #2563eb',
        display: 'grid',
        gap: 0,
        pl: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          borderBottom: '1px solid #e5e7eb',
          display: 'grid',
          gap: 1.5,
          pb: 2.5,
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            color: '#2563eb',
            display: 'inline-flex',
            fontSize: 13,
            fontWeight: 800,
            width: 'fit-content',
          }}
        >
          {getCategoryLabel(announcement.category)}
        </Box>
        <Box
          sx={{
            color: '#111827',
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 800,
            letterSpacing: 0,
            lineHeight: 1.3,
            overflowWrap: 'anywhere',
          }}
        >
          {announcement.title}
        </Box>
        <Box
          sx={{
            color: '#64748b',
            display: 'flex',
            flexWrap: 'wrap',
            fontSize: 13,
            gap: 1.5,
          }}
        >
          <Box component="span">작성자 {getAuthor(announcement)}</Box>
          <Box component="span">작성일 {formatDate(getCreatedAt(announcement))}</Box>
        </Box>
      </Box>
      <Box
        sx={{
          minHeight: 260,
          pt: 3.5,
        }}
      >
        <Box
          sx={{
            color: '#475569',
            fontSize: 13,
            fontWeight: 800,
            mb: 2,
          }}
        >
          내용
        </Box>
        <RichTextContent html={announcement.content} minHeight={220} />
      </Box>
    </Box>
  );
}

function NoticeManagementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  const editId = Number(searchParams.get('id'));
  const isFormMode = mode === 'create' || isEditMode(mode);
  const isDetailViewMode = isDetailMode(mode);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Key[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState<NoticeFormState>(emptyForm);
  const [categoryFilter, setCategoryFilter] = useState<NoticeCategoryFilter>('ALL');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const scrollSentinelRef = useRef<HTMLDivElement | null>(null);
  const canManageNotices = managerRole === 'SUPER' || managerRole === 'MIDDLE';
  const { filteredAnnouncements, hasMoreAnnouncements, visibleAnnouncements } =
    getAnnouncementListState(announcements, categoryFilter, visibleCount);

  const columns: DataTableColumn<Announcement>[] = [
    {
      key: 'managerId',
      header: '작성자',
      sortable: true,
      width: 120,
      render: getAuthor,
    },
    {
      key: 'title',
      header: '제목',
      sortable: true,
      width: 320,
      render: (announcement) => (
        <Box
          onClick={() => navigate(`/notices?mode=detail&id=${announcement.id}`)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              navigate(`/notices?mode=detail&id=${announcement.id}`);
            }
          }}
          role="button"
          sx={{ cursor: 'pointer', maxWidth: 360 }}
          tabIndex={0}
        >
          <Box sx={{ color: '#111827', fontWeight: 700 }}>{announcement.title}</Box>
          <Box
            sx={{
              color: '#64748b',
              fontSize: 11,
              mt: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {toPlainText(announcement.content)}
          </Box>
        </Box>
      ),
    },
    {
      key: 'category',
      header: '분류',
      sortable: true,
      width: 120,
      render: (announcement) => (
        <Chip
          label={announcement.category ? getCategoryLabel(announcement.category) : '-'}
          size="small"
          sx={{
            backgroundColor: '#e8f6fe',
            color: '#0c63a8',
            fontSize: 11,
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      key: 'createdAt',
      header: '작성일',
      sortable: true,
      width: 180,
      render: (announcement) => formatDate(getCreatedAt(announcement)),
    },
    {
      key: 'edit',
      header: '수정',
      align: 'center',
      width: 72,
      render: (announcement) => (
        <IconButton
          aria-label={`${announcement.title} 수정`}
          color="primary"
          onClick={() => navigate(`/notices?mode=edit&id=${announcement.id}`)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];
  const visibleColumns = canManageNotices
    ? columns
    : columns.filter((column) => column.key !== 'edit');

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (managerRole !== null && !canManageNotices && isFormMode) {
      navigate('/notices', { replace: true });
    }
  }, [canManageNotices, isFormMode, managerRole, navigate]);

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    announcementService
      .list()
      .then((data) => {
        if (!ignore) {
          setAnnouncements(data);
          setErrorMessage('');
        }
      })
      .catch((error) => {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error ? error.message : '공지사항 목록을 불러오지 못했습니다.'
          );
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [categoryFilter, filteredAnnouncements.length]);

  useEffect(() => {
    const sentinel = scrollSentinelRef.current;

    if (!sentinel || !hasMoreAnnouncements) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((current) =>
            Math.min(current + VISIBLE_COUNT_STEP, filteredAnnouncements.length)
          );
        }
      },
      { rootMargin: '160px' }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [filteredAnnouncements.length, hasMoreAnnouncements, visibleCount]);

  useEffect(() => {
    if (mode === 'create') {
      setForm(emptyForm);
      setSelectedAnnouncement(null);
      setErrorMessage('');
      return;
    }

    if (!isEditMode(mode) && !isDetailMode(mode)) {
      return;
    }

    if (!Number.isFinite(editId) || editId <= 0) {
      setErrorMessage('수정할 공지사항을 찾을 수 없습니다.');
      return;
    }

    let ignore = false;
    setDetailLoading(true);
    announcementService
      .detail(editId)
      .then((announcement) => {
        if (!ignore) {
          setSelectedAnnouncement(announcement);
          if (isEditMode(mode)) {
            setForm({
              title: announcement.title,
              content: announcement.content,
              category: announcement.category,
            });
          }
          setErrorMessage('');
        }
      })
      .catch((error) => {
        if (!ignore) {
          setErrorMessage(
            error instanceof Error ? error.message : '공지사항 상세 정보를 불러오지 못했습니다.'
          );
        }
      })
      .finally(() => {
        if (!ignore) {
          setDetailLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [editId, mode]);

  const openDeleteConfirm = () => {
    if (!canManageNotices) {
      return;
    }

    if (selectedIds.length === 0) {
      setErrorMessage('삭제할 게시글을 선택해 주세요.');
      return;
    }

    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (!deleting) {
      setDeleteConfirmOpen(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!canManageNotices || selectedIds.length === 0) {
      return;
    }

    const announcementIds = selectedIds.map(Number).filter((id) => Number.isFinite(id));
    setDeleting(true);
    try {
      await announcementService.deleteMany(announcementIds);
      setAnnouncements((current) =>
        current.filter((announcement) => !announcementIds.includes(announcement.id))
      );
      setSelectedIds([]);
      setErrorMessage('');
      setDeleteConfirmOpen(false);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '공지사항 삭제에 실패했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  const submitNotice = async (payload: AnnouncementPayload) => {
    if (!canManageNotices) {
      return;
    }

    setSaving(true);
    try {
      if (mode === 'create') {
        const created = await announcementService.create(payload);
        setAnnouncements((current) => [created, ...current]);
      } else if (isEditMode(mode) && Number.isFinite(editId)) {
        const updated = await announcementService.update(editId, payload);
        setAnnouncements((current) =>
          current.map((announcement) =>
            announcement.id === updated.id ? updated : announcement
          )
        );
      }

      setErrorMessage('');
      navigate('/notices');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '공지사항 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit: NonNullable<ComponentProps<'form'>['onSubmit']> = (event) => {
    event.preventDefault();

    const payload = {
      title: form.title.trim(),
      content: form.content.trim(),
      category: form.category.trim(),
    };

    if (!payload.title || isRichTextEmpty(payload.content) || !payload.category) {
      setErrorMessage('제목, 내용, 분류를 모두 입력해 주세요.');
      return;
    }

    void submitNotice(payload);
  };

  if (isDetailViewMode) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <h1 style={{ margin: 0 }}>공지사항 상세</h1>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canManageNotices && selectedAnnouncement && (
                <Button
                  onClick={() => navigate(`/notices?mode=edit&id=${selectedAnnouncement.id}`)}
                  variant="contained"
                >
                  수정
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate('/notices')}>
                목록으로
              </Button>
            </Box>
          </Box>

          {errorMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              borderColor: '#e2e8f0',
              borderRadius: 1,
              boxShadow: 'none',
              p: { xs: 2.5, sm: 3.5 },
            }}
          >
            <NoticeDetailContent
              announcement={selectedAnnouncement}
              loading={detailLoading}
            />
          </Paper>
        </div>
      </section>
    );
  }

  if (isFormMode) {
    const title = mode === 'create' ? '게시글 작성' : '게시글 수정';

    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <h1 style={{ margin: 0 }}>{title}</h1>
            <Button variant="outlined" onClick={() => navigate('/notices')}>
              목록으로
            </Button>
          </Box>

          {errorMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Paper component="form" onSubmit={handleSubmit} variant="outlined" sx={{ p: 3 }}>
            {detailLoading ? (
              <LoadingState message="공지사항을 불러오는 중입니다." />
            ) : (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <TextField
                  label="제목"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, title: event.target.value }))
                  }
                  required
                  value={form.title}
                />
                <TextField
                  label="분류"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, category: event.target.value }))
                  }
                  required
                  select
                  SelectProps={{ native: true }}
                  value={form.category}
                >
                  {NOTICE_CATEGORY_OPTIONS.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </TextField>
                <RichTextEditor
                  label="내용"
                  minHeight={260}
                  onChange={(content) => setForm((current) => ({ ...current, content }))}
                  value={form.content}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/notices')} variant="outlined">
                    취소
                  </Button>
                  <Button disabled={saving} type="submit" variant="contained">
                    {saving ? '저장 중' : '저장'}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </div>
      </section>
    );
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <div className="page-title-block">
          <p className="page-section">Homepage</p>
          <h1>공지사항 관리</h1>
          <p className="page-description">공지사항과 FAQ 게시글을 작성, 수정, 삭제합니다.</p>
        </div>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 3,
          }}
        >
          {canManageNotices && (
            <Button onClick={() => navigate('/notices?mode=create')} variant="contained">
              글 추가
            </Button>
          )}
        </Box>

        {errorMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            alignItems: { xs: 'stretch', sm: 'center' },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {NOTICE_CATEGORY_FILTERS.map((filter) => {
              const selected = categoryFilter === filter.value;

              return (
                <Button
                  key={filter.value}
                  onClick={() => setCategoryFilter(filter.value)}
                  size="small"
                  variant={selected ? 'contained' : 'outlined'}
                >
                  {filter.label}
                </Button>
              );
            })}
          </Box>
          {canManageNotices && (
            <Button
              color="error"
              disabled={deleting}
              onClick={openDeleteConfirm}
              variant="outlined"
            >
              {deleting ? '삭제 중' : `선택 삭제 (${selectedIds.length})`}
            </Button>
          )}
        </Box>

        {loading ? (
          <LoadingState message="공지사항을 불러오는 중입니다." />
        ) : (
          <>
            <DataTable
              columns={visibleColumns}
              emptyMessage="등록된 게시글이 없습니다."
              onSelectionChange={setSelectedIds}
              rowKey="id"
              rows={visibleAnnouncements}
              selectable={canManageNotices}
              selectedRowKeys={selectedIds}
            />
            {filteredAnnouncements.length > 0 && (
              <Box
                ref={scrollSentinelRef}
                sx={{
                  color: '#64748b',
                  fontSize: 12,
                  py: 2,
                  textAlign: 'center',
                }}
              >
                {hasMoreAnnouncements
                  ? `${visibleAnnouncements.length} / ${filteredAnnouncements.length} 표시 중`
                  : `전체 ${filteredAnnouncements.length}건 표시`}
              </Box>
            )}
          </>
        )}
      </div>
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>
          게시글 삭제
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
            선택한 게시글 {selectedIds.length}건을 삭제할까요?
            <br />
            삭제된 게시글은 목록에서 제거됩니다.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm} disabled={deleting}>
            취소
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            onClick={handleDeleteSelected}
            disabled={deleting}
          >
            {deleting ? '삭제 중' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
}

export default NoticeManagementPage;
